import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Hourglass, PenLine } from "lucide-react";
import {
  Button,
  EmptyState,
  ListCard,
  SearchField,
  SkeletonList,
  SortSelect,
  StatusBadge,
  Timestamp,
} from "@/design-system/components/ui";
import { PREQUAL_INVITE_STATUS_TONE } from "@/design-system/lib/prequalInviteStatus";
import type { PrequalInviteStatus } from "@/design-system/lib/prequalInviteStatus";
import {
  prequalSiteApi,
  type PrequalDraft,
  type PrequalDraftPage,
} from "../../api/prequalSite";
import {
  errorMessage,
  PREQUAL_PAGE_SIZE,
  PREQUAL_SORT_OPTIONS,
  type DraftsPanelProps,
  type PrequalSort,
} from "./panelProps";
import { PrequalPager } from "./PrequalPager";

// Partner Brand Ownership — the "In progress" panel.
//
// The sender view: who started a prequalification on this site and hasn't
// finished, so somebody can pick up the phone. Read-only by design — there is
// no action here that touches the applicant's own answers, and the one control
// that does anything ("Continue with them") opens the assisted form rather than
// editing a row in place.
//
// The rows carry NO answer content. That is not a display choice: the server's
// shared projection has no field that could hold it, on any role and behind any
// flag. Name and email are here because they are identity, and a follow-up list
// of anonymous rows would be useless. Nor is there a resume token — that token
// authorizes every anonymous read and write of the row, so putting it on screen
// would turn a screen-share or a pasted support ticket into a permanent
// credential leak.
//
// Portal-local by the isolation rule: design-system kit + portal-relative
// imports only, and no React Query (plain useState loads, like every other
// portal tab). The Mine/Everyone scope is the SHELL's — read here, never
// written, so the three lists can never be showing different populations.

/** The invite status pill for a draft row.
 *
 *  NULL is a real state — an ORGANIC draft (one nobody sent) has no invite and
 *  therefore no status. A partner is never entitled to one, but the projection
 *  is shared with the staff list where they appear, so this renders the state
 *  rather than trusting the scope to have excluded it. */
function DraftStatusBadge({ status }: { status: PrequalInviteStatus | null }) {
  if (!status) return <StatusBadge tone="muted">Not invited</StatusBadge>;
  return <StatusBadge tone={PREQUAL_INVITE_STATUS_TONE[status]}>{status}</StatusBadge>;
}

/** "Step 3 of 6". The denominator is the row's OWN funnel — a buyer has six
 *  steps and a seller seven — and the numerator is clamped for display because
 *  the server stores the step index inclusive of the end of the funnel. */
function stepLabel(draft: PrequalDraft): string {
  const shown = Math.min(draft.stepIndex + 1, draft.stepCount);
  return `Step ${shown} of ${draft.stepCount}`;
}

/** Keystrokes-to-query delay. Long enough that typing a name is one request,
 *  short enough that the list feels live. */
const SEARCH_DEBOUNCE_MS = 300;

export function DraftsPanel({ mineOnly }: DraftsPanelProps) {
  const [drafts, setDrafts] = useState<PrequalDraftPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  // This panel's OWN list controls. The scope above them belongs to the shell.
  const [rawSearch, setRawSearch] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<PrequalSort>("newest");
  const [page, setPage] = useState(1);

  // Last-request-wins. Changing any control leaves two loads in flight, and the
  // slower one must not repaint the filter the user has already moved off —
  // including when it FAILS, which is why the catch is guarded too rather than
  // only the success arm.
  const requestSeq = useRef(0);

  // Every control that narrows the list returns to page 1 in the SAME update as
  // the change itself, so only one fetch goes out. The shell-owned scope is a
  // prop rather than a handler, so it is reconciled during render — the
  // React-sanctioned adjust-state-on-prop-change shape. An effect would work
  // too, but it would fire a doomed page-3 request first and flash an empty
  // list on the way past.
  const [prevScope, setPrevScope] = useState(mineOnly);
  if (prevScope !== mineOnly) {
    setPrevScope(mineOnly);
    setPage(1);
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQ(rawSearch.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [rawSearch]);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      const result = await prequalSiteApi.drafts({
        mineOnly,
        // Blank is dropped rather than sent as an empty param: an empty box
        // must be indistinguishable from no search at all.
        q: q || undefined,
        sort,
        // Sending these at all is the fix for a live defect — the list used to
        // send no page params, so a site with more than a page of in-progress
        // rows silently showed the first 25 and the fetched total was
        // discarded.
        page,
        pageSize: PREQUAL_PAGE_SIZE,
      });
      if (seq !== requestSeq.current) return;
      setDrafts(result);
      // Cleared on success. A panel that keeps its last error banner above a
      // list that has since loaded is telling the partner two contradictory
      // things at once.
      setError(null);
    } catch (err: unknown) {
      if (seq !== requestSeq.current) return;
      setError(errorMessage(err, "Couldn't load your in-progress prequalifications."));
    }
  }, [mineOnly, q, sort, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = drafts?.data ?? [];
  const searching = q.length > 0;

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <h3 className="m-0 font-display text-lead font-bold text-onyx">In progress</h3>
        <div className="flex flex-wrap items-center gap-space-2">
          {/* No `label` — it defaults to "Search". A label naming the email
              field would collide with the send wizard's own Email input for
              anything looking either of them up by accessible name. */}
          <SearchField
            value={rawSearch}
            onChange={setRawSearch}
            placeholder="Search by name or email…"
            className="w-full sm:w-[260px]"
          />
          {/* The server orders drafts by last activity, so "newest" here means
              most recently touched — which is also the existing DESC default. */}
          <SortSelect
            value={sort}
            onChange={(next) => {
              setSort(next as PrequalSort);
              setPage(1);
            }}
            options={PREQUAL_SORT_OPTIONS}
          />
        </div>
      </div>

      {error && (
        // Rendered INSIDE the panel: the site banner and the tab bar above it
        // are still true and still usable when one list fails to load.
        <p className="m-0 text-small text-status-error" role="alert">
          {error}
        </p>
      )}

      {!drafts ? (
        // First load only. A refetch leaves the rows in place, so typing does
        // not strobe the list between skeleton and results on every keystroke.
        // Suppressed once there is an error to show instead — a skeleton that
        // never resolves reads as a hang rather than as a failure.
        error ? null : <SkeletonList rows={2} data-testid="prequal-drafts-loading" />
      ) : rows.length === 0 ? (
        searching ? (
          <EmptyState
            icon={Hourglass}
            title="No in-progress prequalifications match your search"
            description="Search looks at names and email addresses. Try a different spelling, or clear the box to see everyone who's part-way through."
          />
        ) : (
          <EmptyState
            icon={Hourglass}
            // Worded for the default scope, which is Everyone on this site.
            title="Nobody is part-way through right now"
            description={
              mineOnly
                ? "When someone starts answering, they'll appear here with how far they've got — so you can follow up before they drift. You're only seeing the ones you sent; switch to Everyone on this site to widen it."
                : "When someone opens their invitation and starts answering, they'll appear here with how far they've got — so you can follow up before they drift."
            }
          />
        )
      ) : (
        <>
          <div className="flex flex-col gap-space-3">
            {rows.map((draft) => (
              <DraftRow key={draft.draftId} draft={draft} />
            ))}
          </div>
          {/* Captioned from the SERVER's echo, not the local page state: a
              page change leaves the previous rows on screen until the next
              response lands, and a caption reading "Page 2" above page 1's
              rows is a lie for however long that takes. */}
          <PrequalPager
            page={drafts.page}
            pageSize={drafts.pageSize}
            totalCount={drafts.totalCount}
            onPage={setPage}
          />
        </>
      )}
    </div>
  );
}

function DraftRow({ draft }: { draft: PrequalDraft }) {
  return (
    <ListCard
      data-testid={`prequal-draft-row-${draft.draftId}`}
      title={`${draft.firstName} ${draft.lastName}`}
      // Picking one up is what this queue is FOR: a stale row is worth a phone
      // call, and this is what happens next once somebody makes it. The form
      // opens here and you drive it — nothing about the applicant's own link
      // changes, so they can still finish it themselves.
      actions={
        // The accessible name sits on the LINK — that is what a keyboard or
        // screen-reader user activates, and one row's "Continue" is otherwise
        // indistinguishable from the next.
        <Link
          to={`/portal/prequal-site/assist/${draft.draftId}`}
          aria-label={`Continue with ${draft.firstName} ${draft.lastName}`}
          data-testid={`prequal-draft-assist-${draft.draftId}`}
        >
          <Button type="button" variant="secondary" size="sm" tabIndex={-1}>
            <PenLine size={14} strokeWidth={1.5} aria-hidden className="mr-space-1" />
            Continue with them
          </Button>
        </Link>
      }
      status={
        <span className="flex flex-wrap items-center justify-end gap-space-1">
          <DraftStatusBadge status={draft.inviteStatus} />
          {/* `isAbandoned` is a DISPLAY classification from the server's shared
              staleness threshold, never recomputed here — two derivations of
              "stale" disagree the first time the threshold moves. It is also
              ORTHOGONAL to the invite status: an invite can be perfectly live
              while the person behind it has gone quiet. Both facts, not one
              collapsed into the other. */}
          {draft.isAbandoned && <StatusBadge tone="warn">Needs follow-up</StatusBadge>}
        </span>
      }
      meta={
        <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1">
          <span>{draft.role}</span>
          <span aria-hidden>·</span>
          <span>{draft.email}</span>
          <span aria-hidden>·</span>
          <span>{stepLabel(draft)}</span>
          <span aria-hidden>·</span>
          <span>
            Last activity <Timestamp value={draft.lastActivityAtUtc} />
          </span>
        </span>
      }
    />
  );
}
